// ChatFull — the chat's own tab: the whole history, pure.
//
// Presentation `full` (../presentation.ts): the widest surface over the SAME
// one conversation. Scanning a long history is the browser's own Cmd+F job —
// there is no in-app transcript search; the transcript renders in full.
//
// The "what grounds this + who it's for" surface (grounding + audience + scope)
// lives in the ContextSideCard the host mounts BESIDE this chat, not as chrome
// stacked on top — so visible history and agent context read as two distinct
// objects rather than one growing header.
//
// Composes ChatConversation (the shared transcript-over-composer stack; here
// the transcript centres on the reading measure). Pure/props-driven; chrome
// strings via Lingui; no state, no I/O.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import type { PowerComposerStatus } from "../../Composer/PowerComposer/sendState.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../../Composer/PowerComposer/types.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import {
    ChatConversation,
    type OpeningMoveOfferView,
} from "../ChatConversation/ChatConversation.js";
import type { ResolveInlineLink } from "../InlineLink/InlineLink.js";
import type { ChatAnchor, ChatScope } from "../presentation.js";
import type {
    ChatEntry,
    ChatEntryId,
    ChatFieldAnswer,
    DraftingPhase,
    FormSourceOption,
    InterruptionAction,
} from "../types.js";
import type { WaitingActions } from "../WaitingGroup/WaitingGroup.js";
import "./ChatFull.css";

export type ChatFullProps = {
    readonly conversationId: string;
    // Already projected by the owner: scope applied.
    readonly entries: readonly ChatEntry[];
    readonly scope: ChatScope;
    readonly anchor: ChatAnchor;
    // The in-flight/resolved status trailing the transcript.
    readonly drafting?: DraftingPhase;
    // The one shared draft, controlled by the owner.
    readonly draft: string;
    readonly onDraftChange: (value: string) => void;
    readonly onSubmit: () => void;
    // The composer's send-state machine and @-mention/`/`-command vocabulary
    // — forwarded to ChatConversation's PowerComposer untouched (see its own
    // header note). All optional with inert defaults.
    readonly status?: PowerComposerStatus;
    readonly onStop?: () => void;
    // PS-61: what the author can do with messages that wait (see
    // ChatConversation).
    readonly waitingActions?: WaitingActions;
    readonly onRetry?: () => void;
    // Message-history navigation (A-11) — forwarded untouched to
    // ChatConversation's PowerComposer.
    readonly onRecallOlder?: () => void;
    readonly onRecallNewer?: () => void;
    readonly historyNavActive?: boolean;
    readonly mentions?: readonly ComposerResourceRef[];
    readonly onMentionsChange?: (
        mentions: readonly ComposerResourceRef[],
    ) => void;
    readonly candidates?: readonly ComposerResourceRef[];
    readonly commands?: readonly ComposerSlashCommand[];
    // An optional leading affordance rendered before the composer's text
    // field (the "+" add-a-source menu, `ComposerAttachButton`), forwarded
    // untouched to ChatConversation. Absent ⇒ nothing renders.
    readonly composerLeadingSlot?: ReactNode;
    // Re-drive the agent with a typed instruction (LANE S3) — backs an
    // ActionOffer's `send_prompt` move (retry / re-export). The reflexive pill
    // row it once fed is retired; the name is kept so host wiring carries over.
    readonly onSuggestedResponse?: (text: string) => void;
    // Turn-epilogue form intents (LANE S3), forwarded to information_request
    // cards. INTEGRATOR NOTE: the host (AppShellHost) must wire `onSubmitForm`
    // to `supply_information` and (optionally) `onDismissForm`.
    readonly onSubmitForm?: (
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) => void;
    readonly onDismissForm?: (requestId: string) => void;
    // The library's sources a `field_sources` form question offers (D42).
    readonly formSources?: readonly FormSourceOption[];
    // Proposal pointer intents, forwarded to proposal cards.
    readonly onGoToChange?: (id: ChatEntryId) => void;
    readonly onReviewInPlace?: (id: ChatEntryId) => void;
    readonly onAcceptProposal?: (proposalId: string) => void;
    readonly onDiscardProposal?: (proposalId: string) => void;
    // Open a created material as a tab, forwarded to material cards.
    readonly onOpenMaterial?: (materialId: string) => void;
    // Open/download an exported package, forwarded to export cards.
    readonly onOpenExport?: (exportId: string) => void;
    readonly onDownloadExport?: (exportId: string) => void;
    // Open the shell's file picker for one Source (F9/F15's `request_upload`
    // move), forwarded to action-offer cards.
    readonly onRequestUpload?: (
        sourceId: string,
        sourceTitle: string | undefined,
    ) => void;
    // Open a cited source (cite-B), forwarded to the transcript's agent entries.
    readonly onOpenSource?: (resourceId: string) => void;
    readonly onOpenResource?: (ref: ResourceRef, target?: FocusTarget) => void;
    // Agent markdown links → resource chips (see InlineLink).
    readonly resolveLink?: ResolveInlineLink;
    // The interruption notice's recovery (forwarded to the transcript tail).
    readonly onInterruptionAction?: (action: InterruptionAction) => void;
    // The agent's opening-move offer, forwarded to the empty surface (EXPLICIT-
    // START). Absent ⇒ the generic empty-state hero.
    readonly openingMoveOffer?: OpeningMoveOfferView;
    readonly className?: string;
};

export const ChatFull = ({
    conversationId,
    entries,
    scope,
    anchor,
    drafting,
    draft,
    onDraftChange,
    onSubmit,
    status,
    onStop,
    waitingActions,
    onRetry,
    onRecallOlder,
    onRecallNewer,
    historyNavActive,
    mentions,
    onMentionsChange,
    candidates,
    commands,
    composerLeadingSlot,
    onSuggestedResponse,
    onSubmitForm,
    onDismissForm,
    formSources,
    onGoToChange,
    onReviewInPlace,
    onAcceptProposal,
    onDiscardProposal,
    onOpenMaterial,
    onOpenExport,
    onDownloadExport,
    onRequestUpload,
    onOpenSource,
    onOpenResource,
    resolveLink,
    onInterruptionAction,
    openingMoveOffer,
    className,
}: ChatFullProps) => {
    const { t } = useLingui();

    return (
        <section
            className={`bilda-chat-full${className ? ` ${className}` : ""}`}
            aria-label={t`Assistant`}
        >
            <ChatConversation
                conversationId={conversationId}
                entries={entries}
                scope={scope}
                anchor={anchor}
                drafting={drafting}
                draft={draft}
                onDraftChange={onDraftChange}
                onSubmit={onSubmit}
                status={status}
                onStop={onStop}
                waitingActions={waitingActions}
                onRetry={onRetry}
                onRecallOlder={onRecallOlder}
                onRecallNewer={onRecallNewer}
                historyNavActive={historyNavActive}
                mentions={mentions}
                onMentionsChange={onMentionsChange}
                candidates={candidates}
                commands={commands}
                composerLeadingSlot={composerLeadingSlot}
                onSuggestedResponse={onSuggestedResponse}
                onSubmitForm={onSubmitForm}
                onDismissForm={onDismissForm}
                formSources={formSources}
                onGoToChange={onGoToChange}
                onReviewInPlace={onReviewInPlace}
                onAcceptProposal={onAcceptProposal}
                onDiscardProposal={onDiscardProposal}
                onOpenMaterial={onOpenMaterial}
                onOpenExport={onOpenExport}
                onDownloadExport={onDownloadExport}
                onRequestUpload={onRequestUpload}
                onOpenSource={onOpenSource}
                onOpenResource={onOpenResource}
                resolveLink={resolveLink}
                onInterruptionAction={onInterruptionAction}
                openingMoveOffer={openingMoveOffer}
            />
        </section>
    );
};
